/**
 * Settings → Storage — what is filling this drive
 * (docs/plans/2026-08-05_SETTINGS_STORAGE_TAB.md §5).
 *
 * Renders for orgs AND personal drives (§3A — the mirror is what makes
 * retiring the old static App → Storage tab safe). Gate is server-side
 * membership; there is nothing to self-gate here.
 *
 * Honesty rules baked in (§6, as amended by the 2026-08-05 drive-storage
 * decision and Tier-1 alignment, 2026-08-05_STORAGE_FOLLOWUPS.md §1.1):
 *  - the headline is TOTAL drive storage — live files + reclaimable (Bin /
 *    old versions / quarantine), one counted number, EXACT — the same
 *    figure quota enforcement uses. The du/≈/"Approximate"/"Not measured"
 *    machinery died with the measurement it described. The bar under it is
 *    a STACKED COMPOSITION bar: segments per category + reclaimable, in
 *    the same colors as the row dots — one color language for the page.
 *    Reclaimable is drive storage the user can free, not an exempt annex;
 *    the per-bucket detail lives in the Reclaimable section.
 *  - the versions figure is the §2.8 current-content-excluding one, labelled
 *    an upper bound (dedup overlap)
 *  - no bin-retention claim anywhere (§2.5)
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { Skeleton, useConfirm, Tooltip } from '@papan/core-ui/ui'
import { useRealtimeOptional } from '@papan/realtime/client'
import { filesApi, runEmptyBinJob } from '@papan/files/client'
import { storageApi, type StorageCompositionResponse, type LargestFolder } from '../../client/storage'
import { formatBytes, Section } from '../budget/bits'
import { billingApi } from '@papan/billing/client'
import { folderLabel, shortenPath, TrashGlyph, OpenGlyph, RetentionGlyph, WarnGlyph } from './shared'
import { CategoryFilesModal } from './CategoryFilesModal'
import { DuplicatesModal } from './DuplicatesModal'
import { VersionsModal } from './VersionsModal'
import { ChatStorageSection } from './ChatStorageSection'

/** Category palette — ONE color language for the whole page: these dots
 *  mark the legend rows AND the matching segments of the stacked bar. */
const CATEGORY_COLORS: Record<string, string> = {
  documents: 'bg-blue-500',
  images: 'bg-emerald-500',
  video: 'bg-violet-500',
  miniapp: 'bg-amber-500',
  other: 'bg-zinc-500',
}
/** Reclaimable's segment + row-dot color — distinct from all five categories. */
const RECLAIM_COLOR = 'bg-teal-500'
/** Session attachments' segment + row-dot color (#639) — distinct from the
 *  five categories and from reclaimable, since this bucket is neither. */
const SESSION_ATTACHMENTS_COLOR = 'bg-cyan-500'

/** Fraction of quota at which the page starts offering a way out
 *  (followups §3.5). Matches the first storage alert threshold, so the
 *  banner and the notification agree about what "nearly full" means. */
const UPSELL_AT = 0.8

/**
 * Loading state mirroring the page's real geometry (headline card → five
 * By-type rows → three Reclaimable rows) so the swap to data moves nothing.
 * Same aria-busy pattern as BillingSkeleton: the blocks are decorative; the
 * busy flag is the accessible signal.
 */
function StorageSkeleton() {
  return (
    <div className="max-w-3xl" data-testid="storage-page-skeleton" aria-busy="true" aria-live="polite">
      <div className="h-7 mb-4 flex items-center"><Skeleton className="h-5 w-24" /></div>
      {/* Mirrors the hero's real geometry (16px radius, 8px track) so the
          swap to data moves nothing. */}
      <div className="ws-storage-hero p-4 mb-6 space-y-2.5">
        <div className="flex items-baseline gap-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
      {[5, 3].map((rows, section) => (
        <div key={section} className="mb-6">
          <Skeleton className="h-4 w-20 mb-2" />
          <div className="ws-drive-pill rounded-xl px-4 py-3">
            {Array.from({ length: rows }, (_, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <Skeleton className="w-2.5 h-2.5 rounded-sm" />
                <Skeleton className="h-3.5 flex-1 max-w-40" />
                <span className="flex-1" />
                <Skeleton className="h-3.5 w-16" />
                <Skeleton className="h-3 w-14" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function StorageUsagePage({
  tenantId,
  // Unused since the flat version-retention control moved to its own page
  // (#590 Task 9) — the org/personal mirror on THIS page needs neither the
  // scope flag nor the capability-gate email any more. Both stay on the
  // prop signature (underscore-prefixed to satisfy `noUnusedParameters`) so
  // existing callers — the App.tsx wiring — don't need an unrelated edit.
  personal,
  userEmail: _userEmail,
  onOpenBin,
  onOpenQuarantine,
  onRevealFile,
  onOpenFolder,
  onOpenSubscription,
  onOpenVersionRetention,
}: {
  tenantId?: string
  /** Personal drive — same org/personal mirror every card on this page
   *  takes. */
  personal?: boolean
  /** Unused on this page since the flat version-retention control moved to
   *  its own page (#590 Task 9) — kept so existing callers passing it (the
   *  App.tsx wiring) don't need an unrelated edit. */
  userEmail?: string
  /** Shell-owned navigation, same pattern as OrgBudgetDashboard.onNavigate. */
  onOpenBin?: () => void
  onOpenQuarantine?: () => void
  /** Navigate INTO a folder (largest-folders card, followups §3.1) — unlike
   *  onRevealFile, which selects a row in the parent listing. */
  onOpenFolder?: (folderId: string) => void
  /** Subscription page, for the near-full upsell (followups §3.5). The
   *  target self-gates on capability, so this is safe to offer broadly. */
  onOpenSubscription?: () => void
  /** Navigate to a file's folder with it revealed (`?reveal=` — the
   *  onRevealInDrive pattern). Threaded into the manage modal. */
  onRevealFile?: (folderId: string | null, fileId: string) => void
  /** Dedicated Version-retention settings page (#590 Task 9) — the one-line
   *  link that replaced the flat "keep up to N" control (#452 Task A8). Its
   *  own page self-gates on `manage_org`; absent here just hides the link. */
  onOpenVersionRetention?: () => void
}) {
  const { t } = useTranslation('core-ui')
  const confirm = useConfirm()
  const realtime = useRealtimeOptional()
  const [data, setData] = useState<StorageCompositionResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // An ACTION failure is not a page failure. These were once the same
  // state, so one failed Empty Bin replaced the whole page — headline,
  // bar, every card — with a bare red line that only an unrelated
  // realtime event could clear. The drill-down modal already kept the two
  // apart; the page now does too.
  const [actionError, setActionError] = useState<string | null>(null)
  // #969: an Empty Bin started here is running (its job may take minutes).
  const [emptyingBin, setEmptyingBin] = useState(false)
  const [loading, setLoading] = useState(true)
  // Bumped by the manage modal, the Empty Bin action, and realtime file
  // events, so the totals re-load without a page reopen.
  const [refreshKey, setRefreshKey] = useState(0)
  // Owner/admin only — a member's 403 leaves this null and the card simply
  // doesn't render (folder names are gated exactly like the drill-down's).
  const [folders, setFolders] = useState<LargestFolder[] | null>(null)
  // Upsell target (followups §3.5): the cheapest active plan that would
  // actually raise this tenant's ceiling. Null until the drive is near
  // full — see the effect for why it isn't fetched on every page view.
  const [upsell, setUpsell] = useState<{ name: string; quotaBytes: number } | null>(null)
  const [manage, setManage] = useState<{ key: string } | null>(null)
  const [reviewDuplicates, setReviewDuplicates] = useState(false)
  const [reviewVersions, setReviewVersions] = useState(false)

  useEffect(() => {
    if (!tenantId) { setLoading(false); return }
    let alive = true
    setLoading(true)
    storageApi.getComposition(tenantId)
      .then((d) => { if (alive) { setData(d); setError(null) } })
      .catch((e: any) => { if (alive) setError(e?.message || 'Failed to load storage') })
      .finally(() => { if (alive) setLoading(false) })
    // Separate request because it is separately GATED (owner/admin), not
    // because it is optional data — folding it into the composition
    // response would put folder names behind a membership-only endpoint.
    // Any failure (403 included) just means no card; never a page error.
    storageApi.listLargestFolders(tenantId)
      .then((d) => { if (alive) setFolders(d.folders) })
      .catch(() => { if (alive) setFolders(null) })
    return () => { alive = false }
  }, [tenantId, refreshKey])

  // Live refresh: any file event (upload, delete, restore — from ANY tab or
  // member) re-loads the totals, trailing-debounced so a burst (bulk delete,
  // multi-file upload) costs one refetch. Same event source the drive view
  // subscribes to; optional so the page still renders without the provider.
  useEffect(() => {
    if (!realtime || !tenantId) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsub = realtime.onFileEvent(() => {
      if (timer) return
      timer = setTimeout(() => { timer = null; setRefreshKey((k) => k + 1) }, 1000)
    })
    return () => { if (timer) clearTimeout(timer); unsub() }
  }, [realtime, tenantId])

  // Near-full upsell (followups §3.5). Fetched only once the drive is
  // actually near its ceiling — an unconditional catalog request on every
  // Storage page view would be a request nobody asked for, and the answer
  // is only ever rendered above the threshold anyway.
  //
  // Whitelabel + honesty rules (CLAUDE.md): plan NAME and quota come from
  // the catalog, never a literal; nothing is rendered when BILLING_ENABLED
  // is off, or a self-hosted install would advertise a plan nobody can buy
  // (the same stance the model-access plan layer and the dunning gate
  // take); and no price is shown here — the subscription page renders
  // those with the plan's own currency.
  const nearFull = !!data && data.quotaBytes > 0
    && (data.live.bytes + data.reclaimable.bin.bytes + data.reclaimable.versions.bytes + data.reclaimable.quarantine.bytes
      // Optional-chained: a deploy-skew API response (SPA ahead of the API)
      // can omit this field — treat it as 0 rather than throwing.
      + (data.sessionAttachments?.bytes ?? 0))
      / data.quotaBytes >= UPSELL_AT
  const currentQuota = data?.quotaBytes ?? 0
  useEffect(() => {
    if (!nearFull) { setUpsell(null); return }
    let alive = true
    billingApi.listPlans()
      .then(({ plans, billingEnabled, planAuthority }) => {
        if (!alive) return
        if (!billingEnabled) { setUpsell(null); return }
        // No upsell where an OPERATOR assigns plans. "Default includes 250 GB"
        // is a lie to a customer who cannot buy Default, and the CTA beside it
        // opens a picker that (correctly) offers nothing under manual authority.
        // Same judgement the model-access plan layer already makes: it HIDES
        // rather than upselling under `manual`, because advertising an upgrade
        // nobody can purchase is worse than saying nothing.
        //
        // The near-full BANNER is unaffected and must stay — it renders on the
        // threshold alone, so the warning still reaches a tenant with nowhere to
        // upgrade to. Only the offer to buy is false; "you are nearly full" is
        // true for everyone, and `nearFullHint` (clear the Bin, old versions,
        // duplicates) is the honest advice in its place.
        //
        // `?? 'provider'` because the field is OPTIONAL on the wire: during a
        // rolling deploy an older API instance omits it, and the honest reading
        // of "absent" is the behaviour that shipped before the field existed —
        // not "hide the upsell", which would silently drop the CTA for every
        // provider-billed deployment mid-deploy.
        if ((planAuthority ?? 'provider') !== 'provider') { setUpsell(null); return }
        // Cheapest plan that would genuinely raise the ceiling. A plan at
        // or below the current quota is not an upgrade, and offering it
        // would cost the customer money for nothing. A null quota (the
        // client type is looser than the NOT NULL column) states no
        // storage at all, so it cannot be shown to raise anything.
        const better = plans
          .filter((p): p is typeof p & { storageQuotaBytes: number } =>
            p.storageQuotaBytes != null && p.storageQuotaBytes > currentQuota)
          // Cheapest by the plan's price IN THIS DEPLOYMENT'S CURRENCY, which
          // is what `GET /plans` now quotes. Unpriced plans never reach this
          // list (the route drops them), so the `?? 0` is a type guard rather
          // than a ranking decision.
          .sort((a, b) => (a.basePriceMinor ?? 0) - (b.basePriceMinor ?? 0))[0]
        setUpsell(better ? { name: better.name, quotaBytes: better.storageQuotaBytes } : null)
      })
      .catch(() => { if (alive) setUpsell(null) })
    return () => { alive = false }
  }, [nearFull, currentQuota])

  const handleEmptyBin = async () => {
    if (!data || data.reclaimable.bin.items === 0) return
    const ok = await confirm({
      title: t('settings.storage.emptyBin'),
      message: t('settings.storage.emptyBinConfirm', { size: formatBytes(data.reclaimable.bin.bytes) }),
      type: 'danger',
      confirmText: t('settings.storage.emptyBin'),
    })
    if (!ok) return
    try {
      setActionError(null)
      // #969: a background job — the request answers at once; this waits for the job.
      setEmptyingBin(true)
      await runEmptyBinJob({
        start: (operationId) => filesApi.emptyBinAsync(operationId),
        getStatus: (jobId) => filesApi.getEmptyBinStatus(jobId),
        onOperationEvent: (h) => realtime?.onOperationEvent(h) ?? (() => {}),
        isConnected: () => realtime?.status === 'connected',
      })
      setRefreshKey((k) => k + 1)
    } catch (e: any) {
      setActionError(e?.message || 'Failed to empty the Bin')
      setRefreshKey((k) => k + 1)
    } finally {
      setEmptyingBin(false)
    }
  }

  const handleClearVersions = async () => {
    if (!data || data.reclaimable.versions.items === 0) return
    const ok = await confirm({
      title: t('settings.storage.clearVersions'),
      message: t('settings.storage.clearVersionsConfirm', {
        count: data.reclaimable.versions.items,
        size: formatBytes(data.reclaimable.versions.bytes),
      }),
      type: 'danger',
      confirmText: t('settings.storage.clearVersions'),
    })
    if (!ok) return
    try {
      setActionError(null)
      await storageApi.purgeVersions(tenantId!)
      setRefreshKey((k) => k + 1)
    } catch (e: any) {
      setActionError(e?.message || 'Failed to clear versions')
    }
  }

  if (!tenantId) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noTenant')}</div>
  // Skeleton only on FIRST load — a refresh (realtime, post-delete) keeps
  // the page (and an open modal) mounted instead of flashing placeholders.
  // Jump target for the By-type chat row. A ref, not an `id` + hash link:
  // this page lives inside a scrolling settings pane, and a hash change
  // would also push a history entry for what is a scroll, not navigation.
  const chatSectionRef = useRef<HTMLDivElement>(null)
  /**
   * Scrolls the settings pane — and ONLY the settings pane.
   *
   * `scrollIntoView` was the first attempt and it also scrolls every
   * scrollable ancestor up to the document. Settings is an overlay taller
   * than the viewport, so the browser happily scrolled the PAGE as well,
   * dragging the whole overlay upward and leaving a blank slab of page
   * background below it — which read as "clicking the row opens an empty
   * box". Walking up to the nearest scrollable ancestor and setting its
   * `scrollTop` moves that one element and nothing else.
   */
  const scrollToChatSection = () => {
    const target = chatSectionRef.current
    if (!target) return
    let pane: HTMLElement | null = target.parentElement
    while (pane) {
      const style = getComputedStyle(pane)
      const scrolls = /auto|scroll|overlay/.test(style.overflowY)
      if (scrolls && pane.scrollHeight > pane.clientHeight) break
      pane = pane.parentElement
    }
    // No scrolling ancestor (the pane is short enough to fit, or a test
    // environment with no layout): there is nothing to scroll, and falling
    // back to `scrollIntoView` here would reintroduce the page-scroll bug.
    if (!pane) return
    const top = target.getBoundingClientRect().top - pane.getBoundingClientRect().top + pane.scrollTop
    pane.scrollTo({ top: Math.max(0, top - 12), behavior: 'smooth' })
  }

  if (loading && !data) return <StorageSkeleton />
  if (error || !data) return <div className="p-4 text-sm text-red-400">{error}</div>

  const { quotaBytes, live, categories, reclaimable, sessionAttachments } = data
  const totalFiles = categories.reduce((s, c) => s + c.files, 0)
  // Product decision (2026-08-05, drive-storage semantics): reclaimable IS
  // drive storage — a binned file keeps consuming the drive's usage until
  // permanently deleted, exactly like trash in Google Drive. So the
  // HEADLINE is live + reclaimable, one counted total; the per-bucket
  // detail lives in the Reclaimable section below. The figure is exact —
  // the du/≈/"Approximate" machinery died with Tier-1 alignment
  // (2026-08-05_STORAGE_FOLLOWUPS.md §1.1), and this is now the SAME
  // number quota enforcement uses.
  const reclaimTotal = reclaimable.bin.bytes + reclaimable.versions.bytes + reclaimable.quarantine.bytes
  // Session-private attachments (0218, #639) count toward the enforced quota
  // too (driveStorageUsage sums them into usedBytes) — so they must ride in
  // the headline total, or this page would show a smaller number than what
  // actually blocks the next upload.
  // Optional-chained (deploy-skew guard, see the client type's comment on
  // `sessionAttachments`): an old API response may omit this field.
  const usedTotal = live.bytes + reclaimTotal + (sessionAttachments?.bytes ?? 0)
  // The bar is a STACKED COMPOSITION bar: one segment per non-empty
  // category (legend colors) + one for reclaimable — the same color
  // language as the row dots, so the bar answers "what is filling it",
  // not "how close am I to the line" (the fraction of track filled
  // already says that).
  //
  // Widths are floored, not raw: a byte-accurate 0.005% Documents sliver
  // is invisible and unhoverable. Every non-zero segment gets at least
  // MIN_SEG_PCT of the track, and the LARGE segments absorb the
  // difference — the floor redistributes within the truth, it never
  // inflates it. (Only exception: a near-empty drive whose true total is
  // smaller than the floors — chips win over honesty at that scale.)
  //
  // ⚠️ WHAT THE FILL ADDS UP TO DEPENDS ON WHETHER ANYTHING IS PROMISED,
  // and this note used to claim it was always `usedTotal/quota`. With a
  // promise on the bar the segments are the stored bytes PLUS the unspent
  // promise, measured against `barDenominator` — so a drive holding 1 GB of
  // 5 with 4 GB promised draws a strip that is COMPLETELY full (1 GB stored
  // + 4 GB promised = the whole 5 GB track) under a headline reading
  // "1 GB / 5 GB". (An earlier version of this note said "four fifths",
  // which is the stored part alone.) That is the feature, not a bug: the two hatched
  // textures and the By-type rows say which part is bytes and which is a
  // promise. `nearFull` is unaffected — it still reads `usedTotal/quota`.
  // ── Chat storage (#758) ───────────────────────────────────────────────
  const promised = data.chatStorageAllocated?.promisedBytes ?? 0
  // ── THE OVER-ALLOCATION WARNING, REBUILT (#758; removed 2026-10-16) ───
  //
  // It went through five review rounds in its first life and produced a new
  // user-visible fault in every one — a false alarm on a half-empty drive, a
  // correct warning silenced for the life of the tab by one colleague's
  // upload, a rose band painted over real files, and one organisation's
  // member list rendered under another. The fault was never the arithmetic
  // for long: the answer was being assembled from a tenant-wide total AND
  // per-member headroom, which live in two requests owned by two components
  // on a page that is not keyed on the tenant. Each fix added a condition to
  // that seam and the next round found the hole it left.
  //
  // ⚠️ WHAT MAKES THIS ATTEMPT DIFFERENT IS NOT MORE CARE — it is that the
  // per-member figure turned out not to be needed. The design's sentence
  // compares what is PROMISED (the sum of the limits) against what is FREE,
  // and neither of those asks how much any individual has spent. Every
  // number below is already in `data`, from the one response this page
  // already makes. No second request, no cross-component state, nothing to
  // reset when the tenant changes — so the four faults that came from that
  // seam cannot be written here at all.
  //
  // Each figure, and why it is that and not something else:
  /** Chat bytes that EXIST, tenant-wide and deduplicated. */
  const chatUsedBytes = sessionAttachments?.bytes ?? 0
  /** Everything on the drive that is NOT chat storage. Reclaimable is in it:
   *  those bytes block an upload exactly as live ones do (see `reclaimTotal`
   *  above — it is the same number quota enforcement uses), so they are not
   *  room a promise could be honoured out of. */
  const nonChatBytes = live.bytes + reclaimTotal
  /** The most chat storage could ever grow to. The promise is compared
   *  against THIS and not against the whole drive: 24 members at 1 GB are
   *  promised 24 GB, and on a 32 GB drive already holding 16.6 GB of
   *  documents only 15.4 GB of that can ever be honoured. Comparing 24 to 32
   *  would report no problem at all. */
  const freeForChatBytes = Math.max(0, quotaBytes - nonChatBytes)
  /** Promised and not yet spent — what the By-type row reports. NEGATIVE
   *  when a limit was LOWERED below what a member already holds, which is a
   *  real state and not an error; the row's own `> 0` gate gates it (an
   *  earlier `Math.max(0, …)` here was measured to never fire and removed:
   *  two guards for one property, one of them provably idle). */
  const promisedUnusedBytes = promised - chatUsedBytes
  /** ⚠️ THE ONE NUMBER. By how much the promise cannot be kept — the figure
   *  the sentence states AND the width of the band that says it.
   *
   *  The first rebuild had these as two different quantities: the sentence
   *  computed `promised - free`, while the band was positioned from capacity
   *  to the end of the track and so drew `barDenominator - quotaBytes`. They
   *  agree only when the drive is under quota AND the promise exceeds what is
   *  spent. Review found both exits: an organisation downgraded to a smaller
   *  plan had the band painted 2 GB into its DOCUMENTS segment — real files,
   *  which is verbatim one of the faults that got this feature removed the
   *  first time — while the sentence beside it named a different number.
   *
   *  Feeding both from this single variable is what makes that class of bug
   *  unwritable rather than merely fixed. */
  /** What the drive actually has left RIGHT NOW, after everything stored —
   *  chat's own bytes included. This, not `freeForChatBytes`, is what an
   *  unspent promise competes for. */
  const remainingCapacityBytes = Math.max(0, quotaBytes - nonChatBytes - chatUsedBytes)
  /** The unspent promise, floored. ⚠️ FLOORED HERE AND NOT ONLY AT THE
   *  READERS, because the two figures below are derived from it: a negative
   *  value propagated into them as bytes that do not exist. */
  const unspentPromiseBytes = Math.max(0, promisedUnusedBytes)
  /** The part of the unspent promise the drive could still honour… */
  const honourableUnusedBytes = Math.min(unspentPromiseBytes, remainingCapacityBytes)
  /** …and the part it could not. ⚠️ THESE TWO PARTITION `unspentPromiseBytes`
   *  BY CONSTRUCTION, which is the property the bar depends on.
   *
   *  An earlier version computed this as `promised - freeForChatBytes`
   *  instead. That is the same number whenever the promise exceeds what is
   *  already spent, and WRONG when it does not: with 8 GB stored against a
   *  7 GB promise it drew a 1 GB rose band appended to the 8 GB chat
   *  segment, so the strip accounted for 13 GB on a drive holding 12 — the
   *  same byte range painted twice, once as stored and once as promised.
   *  There was no unspent promise at all in that state, and a review case
   *  of mine had pinned the double count as correct. */
  const unkeepableBytes = unspentPromiseBytes - honourableUnusedBytes
  const overPromised = promised > 0 && unkeepableBytes > 0
  /** The bar's scale. Just the sum of what is actually drawn, so a segment
   *  can never be positioned against a total it is not part of.
   *
   *  ⚠️ `quotaBytes` UNCHANGED when there is no promise (a personal drive, an
   *  organisation with no limits set). Rescaling on used bytes instead would
   *  have quietly changed how an OVER-QUOTA drive draws, which has nothing to
   *  do with this feature. */
  const barDenominator = promised > 0
    ? Math.max(quotaBytes, nonChatBytes + chatUsedBytes + unspentPromiseBytes)
    : quotaBytes

  const MIN_SEG_PCT = 1.0
  const rawSegments = [
    ...categories.map((c) => ({
      key: c.key,
      bytes: c.bytes,
      color: CATEGORY_COLORS[c.key] ?? 'bg-zinc-500',
      label: t(`settings.storage.category.${c.key}`),
    })),
    { key: 'reclaimable', bytes: reclaimTotal, color: RECLAIM_COLOR, label: t('settings.storage.reclaimable') },
    {
      key: 'sessionAttachments',
      bytes: chatUsedBytes,
      color: SESSION_ATTACHMENTS_COLOR,
      label: t('settings.storage.chatStorageUsedRow'),
    },
    // The unspent promise, SPLIT IN TWO and both drawn as ordinary segments.
    //
    // ⚠️ SEGMENTS, NOT AN OVERLAY, and that is the correctness fix rather
    // than a style choice. The design drew the over-capacity part absolutely
    // positioned "over the segments so the category underneath still reads
    // continuously" — but an absolute band is placed by RATIO while these
    // widths go through the floor redistribution below, so the two can never
    // line up, and the band's extent was a different quantity from the one
    // the sentence named. As segments they are measured the same way as
    // everything beside them, and the rose part can no longer land on top of
    // real files: there is nothing underneath it to read.
    //
    // Order matters: honourable first. ⚠️ WHERE IT ENDS IS CAPACITY ONLY
    // WHEN THE PROMISE IS OVERDRAWN — then `honourable` is exactly the
    // remaining capacity, so stored + honourable lands on `quotaBytes` and
    // the colour change marks the ceiling without a separate tick to keep in
    // step. A promise that FITS ends short of capacity, which is honest
    // (there is spare room), and a drive ALREADY past its quota has no
    // honourable part at all, so capacity falls inside the stored segments
    // and nothing marks it — that drive's problem is the headline's
    // `usedTotal/quota` and `nearFull`, not this band. An earlier version of
    // this note claimed the boundary was always capacity; review measured
    // three states where it is not.
    {
      key: 'promisedUnused',
      bytes: honourableUnusedBytes,
      color: 'ws-storage-promised-unused',
      label: t('settings.storage.chatStorageUnusedRow'),
    },
    // ⚠️ ONE LABEL PER NUMBER. The cyan segment is the HONOURABLE part, and
    // for three rounds it shared `chatStorageUnusedRow` with a By-type row
    // showing the WHOLE unspent promise — so hovering the bar said "2.00 GB"
    // while the row under it said "6.00 GB" for the same words and the same
    // swatch. The By-type rows are this bar's legend ("the same color
    // language as the row dots"), so each of these two segments now has its
    // own label AND its own row below.
    {
      key: 'promisedUnkeepable',
      bytes: unkeepableBytes,
      color: 'ws-storage-over-capacity',
      label: t('settings.storage.chatStorageUnkeepable'),
    },
  ]
    .filter((s) => s.bytes > 0)
    .map((s) => ({ ...s, pct: barDenominator > 0 ? (s.bytes / barDenominator) * 100 : 0 }))
  const totalPct = Math.min(100, rawSegments.reduce((sum, s) => sum + s.pct, 0))
  const floorSum = rawSegments.filter((s) => s.pct < MIN_SEG_PCT).length * MIN_SEG_PCT
  const largeSum = rawSegments.filter((s) => s.pct >= MIN_SEG_PCT).reduce((sum, s) => sum + s.pct, 0)
  const largeScale = largeSum > 0 && totalPct > floorSum ? (totalPct - floorSum) / largeSum : 1
  const barSegments = rawSegments.map((s) => ({
    ...s,
    width: s.pct < MIN_SEG_PCT ? MIN_SEG_PCT : s.pct * largeScale,
  }))

  const reclaimRows: Array<{
    key: 'bin' | 'versions' | 'quarantine'
    bytes: number
    items: number
    hint?: string
    /** The row's PERMANENT-delete action, if it has one. */
    destructive?: () => void
    destructiveLabel?: string
    /** The caller lacks the right — render it disabled, not absent. */
    destructiveDenied?: boolean
    /** The action is running (#969: an Empty Bin job) — disabled, with a spinner. */
    destructiveBusy?: boolean
    onOpen?: () => void
    openLabel?: string
  }> = [
    {
      key: 'bin', ...reclaimable.bin,
      // The figure is tenant-wide (it feeds the enforced quota), while the
      // Bin VIEW is permission-filtered per item — so a member reading "4
      // items" here and seeing 1 there is correct but confusing. Disclosed
      // rather than scoped: making this row per-member would decouple the
      // headline from the number the server enforces. Not a count of what
      // they can reach, because computing that needs a per-item permission
      // resolution on every page load — the same N-query shape the review
      // flagged on the drill-down.
      hint: t('settings.storage.binTenantWide'),
      // Shown to everyone but DISABLED without the right (user call): a
      // hidden control teaches nothing, while a greyed one says the action
      // exists and isn't yours. The tooltip carries the reason.
      destructive: handleEmptyBin,
      destructiveLabel: t('settings.storage.emptyBin'),
      destructiveDenied: !(data.can?.emptyBin ?? true),
      destructiveBusy: emptyingBin,
      onOpen: onOpenBin, openLabel: t('settings.storage.openBin'),
    },
    {
      key: 'versions', ...reclaimable.versions,
      hint: t('settings.storage.versionsUpperBound'),
      destructive: handleClearVersions,
      destructiveLabel: t('settings.storage.clearVersions'),
      destructiveDenied: !(data.can?.clearVersions ?? true),
    },
    {
      key: 'quarantine', ...reclaimable.quarantine,
      onOpen: onOpenQuarantine, openLabel: t('settings.storage.openQuarantine'),
    },
  ]

  return (
    <div className="max-w-3xl">
      <h2 className="text-lg font-medium themed-text mb-4">{t('settings.storage.title')}</h2>

      {/* Inline, dismissible, and above the data it failed to change — the
          page stays usable and the next attempt clears it. */}
      {actionError && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3">
          <p className="text-sm text-red-300 flex-1">{actionError}</p>
          <button
            onClick={() => setActionError(null)}
            aria-label={t('common:close')}
            className="text-red-300/70 hover:text-red-300 text-xs shrink-0"
          >
            ✕
          </button>
        </div>
      )}

      {/* Headline — the page's HERO (DESIGN_DOC rule 4): the one figure the
          whole page exists to state, so it gets the glass treatment while
          the lists below stay quiet. */}
      <div className="ws-storage-hero p-4 mb-6">
        <div className="flex items-baseline gap-2 text-sm">
          <span className="themed-text">{formatBytes(usedTotal)}</span>
          <span className="themed-text-muted text-xs">/ {formatBytes(quotaBytes)}</span>
          {/* A separate fact from "what is stored", so a separate clause
              rather than a bigger headline number. */}
          {promised > 0 && (
            <>
              <span className="themed-text-muted text-xs">·</span>
              <span className="themed-text-muted text-xs">
                {t('settings.storage.chatStoragePromised', { promised: formatBytes(promised) })}
              </span>
            </>
          )}
        </div>
        {/* Continuous thin strip (the chips/gaps variant was tried and
            rolled back at review) — only the WIDTH FLOOR survives from it,
            so tiny types stay visible without breaking the strip look. */}
        {/* `overflow-hidden` clips the strip's ends, so the tooltip must
            NOT be a child of it — the Tooltip portals to <body>, which is
            what makes hover labels possible on a 1.5px-tall bar at all. */}
        <div className="ws-storage-track mt-2.5 h-2 w-full rounded-full overflow-hidden flex">
          {barSegments.map((s) => (
            // Width lives on this wrapper, not on the Tooltip: the shared
            // component takes no style prop, and widening its API for one
            // caller is the wrong trade.
            <div key={s.key} className="h-full" style={{ width: `${s.width}%` }}>
              <Tooltip
                content={`${s.label} — ${formatBytes(s.bytes)}`}
                always
                placement="top"
                className="block h-full w-full"
              >
                {/* role+aria-label because swapping native `title` for a
                    portal tooltip would otherwise DELETE this segment from
                    the accessibility tree — `title` was carrying the only
                    description a screen reader had for the bar. */}
                <div
                  className={`h-full w-full ${s.color} hover:brightness-125`}
                  role="img"
                  aria-label={`${s.label} — ${formatBytes(s.bytes)}`}
                />
              </Tooltip>
            </div>
          ))}
        </div>
        {/* Says what is true and what is NOT true, in that order. An admin
            reading "over capacity" assumes something is broken or blocked;
            neither is the case, and the sentence has to say so before they
            go looking for a problem to fix. */}
        {overPromised && (
          <p className="themed-text-muted mt-2.5 flex items-start gap-1.5 text-xs leading-relaxed">
            <span className="mt-0.5 shrink-0 text-rose-500" aria-hidden="true"><WarnGlyph size={13} /></span>
            <span>
              {t('settings.storage.chatStorageOverPromised', {
                promised: formatBytes(promised),
                free: formatBytes(freeForChatBytes),
                // The SAME variable the rose segment's width comes from.
                over: formatBytes(unkeepableBytes),
              })}
            </span>
          </p>
        )}
      </div>

      {/* Near-full (followups §3.5). Renders on the threshold alone, so a
          tenant with nowhere to upgrade to — top plan, billing disabled,
          self-hosted — still gets the warning, just without a CTA that
          would go nowhere. The plan name and its quota come from the
          catalog; prices live on the subscription page in the plan's own
          currency (whitelabel rules). */}
      {nearFull && (
        <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <p className="text-sm themed-text">
            {t('settings.storage.nearFull', { pct: Math.min(100, Math.floor((usedTotal / quotaBytes) * 100)) })}
          </p>
          <p className="themed-text-muted text-xs mt-1">
            {upsell
              ? t('settings.storage.nearFullUpgrade', { plan: upsell.name, size: formatBytes(upsell.quotaBytes) })
              : t('settings.storage.nearFullHint')}
          </p>
          {upsell && onOpenSubscription && (
            <button
              onClick={onOpenSubscription}
              className="mt-2 text-xs themed-text underline underline-offset-2 hover:themed-text-secondary"
            >
              {t('settings.storage.seePlans')}
            </button>
          )}
        </div>
      )}

      {/* A drive with nothing in it gets one friendly line, not five zero
          rows and an empty reclaim table. */}
      {totalFiles === 0 && reclaimTotal === 0 && (sessionAttachments?.bytes ?? 0) === 0 ? (
        <div className="themed-bg-secondary rounded-lg p-6 text-center">
          <p className="themed-text-muted text-sm">{t('settings.storage.emptyDrive')}</p>
        </div>
      ) : (
      <>
      <Section title={t('settings.storage.byType')}>
        <div className="ws-drive-pill rounded-xl px-4 py-3">
          {categories.map((c) => (
            <div key={c.key} className="flex items-center gap-3 py-1.5 text-sm">
              <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${CATEGORY_COLORS[c.key] ?? 'bg-zinc-500'}`} />
              <span className="themed-text flex-1 min-w-0 truncate">{t(`settings.storage.category.${c.key}`)}</span>
              <span className="themed-text tabular-nums">{c.bytes > 0 || c.files > 0 ? formatBytes(c.bytes) : '—'}</span>
              {/* Non-empty counts open the manage modal (drill-down +
                  deletion); an empty bucket has nothing to manage. */}
              {c.files > 0 ? (
                <button
                  data-testid={`storage-count-${c.key}`}
                  onClick={() => setManage({ key: c.key })}
                  className="themed-text-muted text-xs tabular-nums w-20 text-right underline underline-offset-2 hover:themed-text transition-colors"
                >
                  {t('settings.storage.fileCount', { count: c.files })}
                </button>
              ) : (
                <span className="themed-text-muted text-xs tabular-nums w-20 text-right">
                  {t('settings.storage.fileCount', { count: c.files })}
                </span>
              )}
            </div>
          ))}
          {/* Chat storage — TWO rows again: bytes that exist, and allowance
              promised and not yet spent. The second one came out with the
              over-allocation warning in 07e5b964b and comes back with it.
              ⚠️ WHICH PUTS THE LABEL'S QUALIFIER BACK TOO. "Chat storage,
              used" was dropped in full review 43C, C13 because it "then
              distinguished this row from nothing" — that reason held while
              this was the only chat row and stops holding the moment the
              unused one returns: unqualified beside qualified reads as
              though one of them were the total. The 43C objection that
              still stands is that the same string labels the bar segment,
              and there it is also exactly right: that segment IS the used
              bytes. */}
          {/* The one By-type row whose last column does NOT open the manage
              modal: chat files are not browsable from here (an admin cannot
              see another member's chat files at all), so it scrolls DOWN to
              the chat storage section on this same page. Same shape as the
              "7 files" links above — a whole-row button was tried first and
              made this the only row behaving differently from its siblings. */}
          <div className="flex items-center gap-3 py-1.5 text-sm">
            <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${SESSION_ATTACHMENTS_COLOR}`} />
            <span className="themed-text flex-1 min-w-0 truncate">{t('settings.storage.chatStorageUsedRow')}</span>
            <span className="themed-text tabular-nums">{formatBytes(chatUsedBytes)}</span>
            <button
              type="button"
              data-testid="storage-bytype-chat"
              onClick={scrollToChatSection}
              className="themed-text-muted text-xs tabular-nums w-20 text-right underline underline-offset-2 hover:themed-text transition-colors"
            >
              {t('settings.storage.chatStorageManageRow')}
            </button>
          </div>
          {/* The unspent promise, as TWO rows mirroring the bar's two bands.
              Absent when nothing is promised, so a drive with no limits set
              shows exactly the rows it did.
              No file count and no action on either: these bytes do not exist
              yet, so there is nothing to count and nothing to browse — the
              dash is the honest cell.
              ⚠️ TWO ROWS, NOT ONE. The design had a single "Chat storage,
              unused" row for the whole unspent promise, drawn when the bar
              had a single cyan band and the overflow was an overlay. The
              band had to be split for the overlay to go (see the segments
              above), and a legend that does not match the bar it explains is
              worse than a longer legend: with one row the page showed a cyan
              swatch beside a figure that was partly rose on the strip. */}
          {honourableUnusedBytes > 0 && (
            <div className="flex items-center gap-3 py-1.5 text-sm" data-testid="storage-bytype-chat-unused">
              <span className="ws-storage-promised-unused w-2.5 h-2.5 rounded-sm shrink-0" />
              <span className="themed-text flex-1 min-w-0 truncate">{t('settings.storage.chatStorageUnusedRow')}</span>
              <span className="themed-text tabular-nums">{formatBytes(honourableUnusedBytes)}</span>
              <span className="themed-text-muted text-xs tabular-nums w-20 text-right" aria-hidden="true">—</span>
            </div>
          )}
          {unkeepableBytes > 0 && (
            <div className="flex items-center gap-3 py-1.5 text-sm" data-testid="storage-bytype-chat-unkeepable">
              <span className="ws-storage-over-capacity w-2.5 h-2.5 rounded-sm shrink-0" />
              <span className="themed-text flex-1 min-w-0 truncate">{t('settings.storage.chatStorageUnkeepable')}</span>
              <span className="themed-text tabular-nums">{formatBytes(unkeepableBytes)}</span>
              <span className="themed-text-muted text-xs tabular-nums w-20 text-right" aria-hidden="true">—</span>
            </div>
          )}
        </div>
      </Section>

      {/* Largest folders (followups §3.1) — the same "what is filling this
          drive" question as By type, answered by LOCATION. Subtree totals,
          so a parent and its heavy child can both appear; the path shows
          why. Owner/admin only: absent for members, and absent on an empty
          drive because the rollup returns nothing. */}
      {folders && folders.length > 0 && (
        <Section title={t('settings.storage.folders.title')} desc={t('settings.storage.folders.desc')}>
          <div className="ws-drive-pill rounded-xl px-4 py-3">
            {folders.map((f) => {
              const label = folderLabel(f.path)
              return (
                <div key={f.folderId} className="flex items-center gap-3 py-1.5 text-sm">
                  <span className="themed-text flex-1 min-w-0 truncate" title={label}>
                    {onOpenFolder ? (
                      <button
                        onClick={() => onOpenFolder(f.folderId)}
                        className="max-w-full truncate underline underline-offset-2 decoration-transparent hover:decoration-inherit transition-colors"
                      >
                        {shortenPath(label, 44)}
                      </button>
                    ) : shortenPath(label, 44)}
                  </span>
                  <span className="themed-text tabular-nums">{formatBytes(f.bytes)}</span>
                  <span className="themed-text-muted text-xs tabular-nums w-20 text-right">
                    {t('settings.storage.fileCount', { count: f.files })}
                  </span>
                </div>
              )
            })}
          </div>
        </Section>
      )}

      <Section
        title={t('settings.storage.reclaimable')}
        desc={t('settings.storage.reclaimHint')}
      >
        <div className="ws-drive-pill rounded-xl px-4 py-3">
          {reclaimRows.map((r) => (
            <div key={r.key} className="flex items-center gap-3 py-1.5 text-sm">
              {/* Same color as the bar's reclaimable segment — the three rows
                  collectively ARE that segment. */}
              <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${RECLAIM_COLOR}`} />
              {/* The upper-bound caveat (§6.5) rides as a hover tooltip only —
                  inline it crowded the row (user call, 2026-08-06). Now the
                  Papan tooltip rather than the browser's, so it matches the
                  read-only explanations in the modals. */}
              <Tooltip content={r.hint ?? null} always placement="top" className="flex-1 min-w-0">
                <span className="themed-text block truncate">
                  {t(`settings.storage.reclaim.${r.key}`)}
                </span>
                {/* The caveat is hover-only visually, but must still reach
                    a screen reader — a portal tooltip never does. */}
                {r.hint && <span className="sr-only">{r.hint}</span>}
              </Tooltip>
              <span className="themed-text tabular-nums">{r.bytes > 0 ? formatBytes(r.bytes) : '—'}</span>
              {/* The versions count opens the review modal — the row's Clear
                  action would otherwise be a destructive verb over an
                  uninspectable number ("65 items" of what?). Same clickable-
                  count grammar as the By-type rows. */}
              {/* The review LISTING is `manage_file_versions:read` (#1102 item 4) — a member without it
                  gets plain text instead of a link into a 403; the purge inside needs write. */}
              {r.key === 'versions' && r.items > 0 && (data.can?.reviewVersions ?? data.can?.clearVersions ?? true) ? (
                <button
                  data-testid="storage-versions-review"
                  onClick={() => setReviewVersions(true)}
                  className="themed-text-muted text-xs tabular-nums w-20 text-right underline underline-offset-2 hover:themed-text transition-colors"
                >
                  {t('settings.storage.itemCount', { count: r.items })}
                </button>
              ) : (
                <span className="themed-text-muted text-xs tabular-nums w-20 text-right">
                  {t('settings.storage.itemCount', { count: r.items })}
                </span>
              )}
              {/* Actions are ICON-ONLY, with the label on hover via the Papan
                  tooltip. Two fixed slots (destructive, then navigational)
                  keep the bytes/items columns aligned across rows regardless
                  of which actions a row carries — a row with no destructive
                  action still reserves its slot.

                  `aria-label` is NOT optional here: an icon button has no
                  text node, so it is the button's only accessible name, and
                  the tooltip (a portal) never reaches assistive tech. It
                  also keeps the e2e's getByRole(name) selectors working. */}
              <span className="shrink-0 flex items-center gap-1 justify-end">
                <span className="w-7 flex justify-end">
                  {/* Empty Bin / Clear old versions — the relief valves for
                      counted reclaimable bytes. Only when there is
                      something to act on. */}
                  {r.destructive && r.items > 0 && (
                    <Tooltip
                      content={r.destructiveDenied ? t('settings.storage.noPermission') : r.destructiveBusy ? t('settings.storage.emptyingBin') : r.destructiveLabel!}
                      always
                      placement="top"
                      className="inline-flex"
                    >
                      <button
                        onClick={r.destructive}
                        disabled={r.destructiveDenied || r.destructiveBusy}
                        aria-label={r.destructiveLabel}
                        aria-busy={r.destructiveBusy || undefined}
                        className={`p-1.5 rounded-md transition-colors ${
                          r.destructiveDenied
                            ? 'themed-text-muted opacity-40 cursor-not-allowed'
                            : r.destructiveBusy
                              ? 'text-red-400 cursor-wait'
                              : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                        }`}
                      >
                        {r.destructiveBusy ? <span aria-hidden className="block w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" /> : <TrashGlyph />}
                      </button>
                    </Tooltip>
                  )}
                </span>
                <span className="w-7 flex justify-end">
                  {/* Old-versions row: the navigational slot carries the
                      "manage version retention" action (#590) — the retention
                      POLICY lives next to the reclaimable bytes it governs,
                      right of the red Trash. Shown regardless of item count
                      (it is a setting, not a fact about current data). Other
                      rows keep their Open action. */}
                  {r.key === 'versions' && onOpenVersionRetention ? (
                    <Tooltip content={t('settings.storage.manageRetention')} always placement="top" className="inline-flex">
                      <button
                        data-testid="storage-manage-retention"
                        onClick={onOpenVersionRetention}
                        aria-label={t('settings.storage.manageRetention')}
                        className="p-1.5 rounded-md themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors"
                      >
                        <RetentionGlyph />
                      </button>
                    </Tooltip>
                  ) : r.onOpen ? (
                    <Tooltip content={r.openLabel!} always placement="top" className="inline-flex">
                      <button
                        onClick={r.onOpen}
                        aria-label={r.openLabel}
                        className="p-1.5 rounded-md themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors"
                      >
                        <OpenGlyph />
                      </button>
                    </Tooltip>
                  ) : null}
                </span>
              </span>
            </div>
          ))}
        </div>
      </Section>


      {/* Duplicates (followups §2.1) — content addressing makes exact
          duplicates one grouped query, so the card is free to show. Only
          renders when there IS something to review. */}
      {data.duplicates.sets > 0 && (
        <Section title={t('settings.storage.duplicates.title')} desc={t('settings.storage.duplicates.desc')}>
          <div className="ws-drive-pill rounded-xl px-4 py-3">
            <div className="flex items-center gap-3 py-1.5 text-sm">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0 bg-rose-500" />
              <span className="themed-text flex-1 min-w-0 truncate">
                {t('settings.storage.duplicates.summary', {
                  // `count`, not `sets` — i18next picks the plural form
                  // from `count` alone, so a custom var renders "1 sets".
                  count: data.duplicates.sets,
                  size: formatBytes(data.duplicates.wastedBytes),
                })}
              </span>
              {/* The duplicates LISTING is owner/admin (names cross
                  folder-permission boundaries), but this card rides on the
                  membership-gated aggregate — so without the flag every
                  member got a Review button that could only 403. Same
                  treatment the versions count already had. */}
              {(data.can?.reviewDuplicates ?? true) && (
                <button
                  onClick={() => setReviewDuplicates(true)}
                  data-testid="storage-duplicates-review"
                  className="text-xs themed-text-secondary hover:themed-text underline underline-offset-2"
                >
                  {t('settings.storage.duplicates.review')}
                </button>
              )}
            </div>
          </div>
        </Section>
      )}
      </>
      )}

      {/* Chat storage (#758). This REPLACES the one read-only row that used
          to live here — the same bucket, now with the per-member figures and
          the limits behind it. It renders whether or not there are bytes
          yet: an admin setting limits for a team that has uploaded nothing
          still needs the list, and the old `> 0` guard would hide the whole
          control surface on exactly the drive where it is cheapest to get
          the limits right.

          MOUNTED OUTSIDE the empty-drive branch, which is what makes that
          true. Dropping the `> 0` guard did not achieve it on its own: the
          whole fragment above is the "has something in it" arm of the
          empty-drive ternary, so on a new org with six members and no
          uploads the page rendered one line — "This drive is empty" — and
          the limits were silently in force with no way to see or set them
          (full review 3C, finding 1). The section hides itself on a 403.
          `tenantId` is required by the routes. The personal mirror DOES
          have a membership row — an earlier note here claimed it did not,
          and that wrong claim is why the org/member toggle appeared on
          personal drives; `personal` now settles it at the source. */}
      {tenantId && (
        <div ref={chatSectionRef}>
        <ChatStorageSection
          tenantId={tenantId}
          // Changing a limit moves the "X promised to members" line in
          // the hero, which reads this page's own response — so the page
          // refetches on a save. The section refetches itself on the
          // same save. Without this the section's list updated while the
          // line above it kept the old total, which reads as the save
          // not having worked.
          onLimitsChanged={() => setRefreshKey((k) => k + 1)}
          // The Adjust-limit dialog shows what a change does to the
          // ORGANISATION's commitment, not just to one member — these two
          // are already on this page's response, so they are passed down
          // rather than fetched a second time by the dialog.
          quotaBytes={quotaBytes}
          promisedBytes={data.chatStorageAllocated?.promisedBytes ?? null}
          // D13: the same CAPABILITY test the server applies, split read from
          // write, so the toggle and the org actions appear exactly when the
          // routes behind them would answer. `clearVersions` is the fallback
          // for a deploy-skew response that predates these two fields — it
          // is the old owner/admin role flag, which is what the capability's
          // defaults reproduce anyway.
          //
          // The `?? false` tail is DELIBERATELY unlike the `?? true` the
          // other flags on this page use — see the client type's note for
          // the trade. Do not "make it consistent" without reading it.
          isAdmin={data.can?.viewChatStorage ?? data.can?.clearVersions ?? false}
          canManage={data.can?.manageChatStorage ?? data.can?.clearVersions ?? false}
          personal={personal ?? false}
        />
        </div>
      )}

      {/* Version retention (#590) — the primary entry point is now the
          RetentionGlyph icon button on the reclaimable "Old versions" row
          above (the policy sits beside the bytes it governs). This standalone
          link is the FALLBACK for the empty-drive branch only, where the
          reclaimable section (and so that icon) does not render — a policy
          setting must stay reachable even on a drive with nothing in it yet.
          Absent `onOpenVersionRetention` hides it rather than rendering a
          dead link. */}
      {onOpenVersionRetention && totalFiles === 0 && reclaimTotal === 0 && (
        <p className="mb-6 -mt-2">
          <button
            onClick={onOpenVersionRetention}
            className="text-xs themed-text-secondary underline underline-offset-2 hover:themed-text"
          >
            {t('settings.storage.manageRetention')}
          </button>
        </p>
      )}

      {manage && (
        <CategoryFilesModal
          tenantId={tenantId}
          category={manage.key}
          categoryLabel={t(`settings.storage.category.${manage.key}`)}
          onClose={() => setManage(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          onOpenFile={onRevealFile}
        />
      )}
      {reviewDuplicates && (
        <DuplicatesModal
          tenantId={tenantId}
          onClose={() => setReviewDuplicates(false)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          onOpenFile={onRevealFile}
        />
      )}
      {reviewVersions && (
        <VersionsModal
          tenantId={tenantId}
          canPurge={data?.can?.clearVersions ?? true}
          onClose={() => setReviewVersions(false)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          onOpenFile={onRevealFile}
        />
      )}
    </div>
  )
}
